import mutations from './geofleet.mutations'
import actions from './geofleet.actions'
import { buildVehicleAlerts } from '@/functions/checkVehicleAlerts'
import moment from 'moment'

const state = () => ({
    trucks: [],
    // REFACTOR-009: `trucksRef` rimosso completamente. Era il doppio storage del
    // lookup id→truck. Sostituito dal getter `trucksMap` (vedi sotto) derivato da
    // state.trucks — single source of truth. La mutation setTrucksRef + tutti i
    // consumer (VehiclesCrossResults, DetailsBody, GeoFleet/Mobile bootstrap)
    // sono stati migrati in Sprint 6 Fase 2 (A, B, C).
    truckFilteredOut: [],
    truck_hooked_list: [],
    trucksEvents: [],
    trucksLastEventId: 14,
    drivers: {},
    driversData: {},
    driversPlafondData: {},
    companies: {},
    mainMapStopAutocenter: false,
    mainMapStopAutoFitBounds: false,
    mainMapInstance: null,
    // REFACTOR-007 (Sprint 7): bus per il focus imperativo cross-componente.
    // Sostituisce le chiamate dirette $refs.map.truckFocus / $refs.main_panel.truckFocus
    // fatte da GeoFleet. GeoFleet committa `requestTruckFocus`; GeofleetMap e MainPanel
    // watchano il getter `getFocusRequest` e applicano l'imperativo nel proprio contesto
    // (setView mappa / scroll lista), dove vivono la Leaflet instance e lo scroller.
    // La reference cambia a ogni richiesta (anche stesso truck due volte) così il
    // watcher dei figli ri-triggera sempre. Consumato dalle Fasi 3-4.
    focusRequest: null,
    // Bus for the /location?x=..&y=.. route. LocationPage commits this
    // (with the resolved address as label); GeofleetMap watches it and
    // populates its search bar / map view. Shape: { lat, lng, label } | null.
    locationRequest: null,
    mainMapExtraObjects: {
        markers: [],
        circles: [],
        polygons: [],
        lines: []
    },
    gmaps: null,
    companyTags: [],
    filtersDictionary: {
        parkings: [],
        stations: []
    },
    // config / companyConfig migrati al modulo namespaced `config` (REFACTOR-030,
    // Sprint 16): lo stato vive in state.config.user / state.config.company.
    vehiclesCrossSearch: {},
    vehiclesCrossResults: [],
    // One-shot seed consumed by VehiclesCross on open (map context menu
    // "see vehicles history"). Holds { address: { label, referenceCoordinate } }.
    vehiclesCrossSeed: null,
    // One-shot seed consumed by MainPanel (map context menu "find near
    // vehicles"). Holds { location: { label, referenceCoordinate } }.
    nearVehiclesSeed: null,
    bluetooth_devices: []
})

const getters = {
    getFocusTruck: (state) => {
        return state.trucks.find((t) => t.on_focus) || null
    },
    getActivityTruck: (state) => {
        return state.trucks.find((t) => t.on_activity) || null
    },
    // REFACTOR-007 (Sprint 7): vedi state.focusRequest. Consumato dai watcher
    // di GeofleetMap (setView mappa) e MainPanel (scroll lista) nelle Fasi 3-4.
    getFocusRequest: (state) => state.focusRequest,
    getLocationRequest: (state) => state.locationRequest,
    // REFACTOR-009: indice O(1) by id, derivato da state.trucks.
    // Vuex cacha il getter — ricalcolato solo quando state.trucks è riassegnata
    // (tutte le mutation trucks fanno reassignment immutabile, vedi mutations).
    // Sostituisce il pattern `this.trucks_ref` locale di GeoFleet/Mobile + il
    // doppio storage `state.trucksRef` per il lookup id→truck.
    // Cost: O(N) build a ogni cambio (~1000 Map.set su flotta 1000 ogni 20s).
    //
    // REFACTOR-024 (Sprint 14, INVESTIGATION-001 F5) — VALUTATO, NON implementato:
    // si potrebbe mantenere una Map in state e patcharla incrementalmente in
    // mergeTrucksDelta/appendTruck invece di ricostruirla da zero qui. Ma il costo
    // reale è marginale (~660 Map.set sub-ms) e introdurrebbe un secondo stato da
    // tenere sincronizzato con state.trucks → rischio sulla single-source-of-truth
    // di Sprint 6 (REFACTOR-009) per un guadagno trascurabile. Lasciato derivato.
    trucksMap: (state) => {
        const map = new Map()
        state.trucks.forEach((t) => {
            if (t && t.id != null) map.set(t.id, t)
        })
        return map
    },
    // Method-style getter: invocazione `store.getters.getTruckById(id)`.
    // Wrapper su trucksMap per ergonomia chiamante.
    getTruckById: (state, getters) => (id) => getters.trucksMap.get(id) || null,
    getArrayOfDriversData: (state) => {
        var tmpArray = []

        for (const [key, value] of Object.entries(state.driversData)) {
            value.id = key
            tmpArray.push(value)
        }

        return tmpArray
    },
    getTrucksEventsSinceLastEventId: (state) => {
        if (state.trucksLastEventId <= 1) return []

        return state.trucksEvents.filter((event) => event.id > state.trucksLastEventId)
    },
    getTrucksWithAlerts: (state) => {
        let trucksWithAlerts = []

        // REFACTOR-023 (Sprint 14): `now` allocato una sola volta e passato a tutti
        // i veicoli, invece di un moment() per veicolo dentro buildVehicleAlerts.
        // Su flotta 660 questo getter ricalcola a ogni polling (lo legge il badge
        // indicatore MainPanel:108, sempre montato). NB: l'ottimizzazione è solo
        // sulle allocazioni; il getter continua a ricalcolare TUTTI i veicoli a ogni
        // tick di proposito — alcuni alert sono time-based (no_points >24h,
        // returnToTheFuture, tacho-clock) e cambiano senza un nuovo lastpoint, quindi
        // NON sono precalcolabili solo sul delta. INVESTIGATION-001 F4.
        const now = moment()

        state.trucks.forEach((truck) => {
            let alerts = buildVehicleAlerts(truck, now)

            if (Object.entries(alerts).length > 0) {
                trucksWithAlerts.push({
                    ...truck,
                    alerts: alerts
                })
            }
        })

        return trucksWithAlerts
    },
    getBluetoothDevicesByVehicle: (state) => {
        const devicesByVehicle = {}
        state.bluetooth_devices.forEach((device) => {
            if (!devicesByVehicle[device.vehicle]) {
                devicesByVehicle[device.vehicle] = []
            }
            devicesByVehicle[device.vehicle].push(device)
        })
        return devicesByVehicle
    },
    getAppActions: (state) => {
        return {
            filtersActive: state.truckFilteredOut.length > 0,
            mainMapStopAutocenter: state.mainMapStopAutocenter,
            mainMapStopAutoFitBounds: state.mainMapStopAutoFitBounds
        }
    },
    getDriversPlafondData: (state) => {
        return state.driversPlafondData
    },
    getVehicleHooked: (state) => (plate) => {
        const obj = state.truck_hooked_list.find((e) => e.truck === plate || e.trailer === plate)
        return obj?.truck === plate ? obj.trailer : obj.truck
    },
    getTruckFromTrailerHooked: (state) => (plate) => {
        const obj = state.truck_hooked_list.find((e) => e.trailer === plate) || null
        return obj ? obj.truckObj : null
    },
    getTypeVehicleHooked: (state) => (plate) => {
        const obj = state.truck_hooked_list.find((e) => e.truck === plate || e.trailer === plate)
        return obj?.truck === plate ? 'truck' : 'trailer'
    },
    isTruckHooked: (state) => (plate, source) => {
        return state.truck_hooked_list.some(
            (e) =>
                e.truck === plate ||
                (e.trailer === plate &&
                    moment().diff(moment.unix(e.trailer_timestamp / 1000), 'minutes') < 7)
        )
    }
}

export default {
    state,
    getters,
    mutations,
    actions
}
